import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ChevronLeft,
  Anchor,
  Users,
  Calendar,
  Settings,
  Link2,
  Smartphone,
  Share2,
  Download,
  RefreshCw,
  AlertTriangle,
  Cylinder as CylinderIcon,
  Clock,
  Edit2,
  Eye,
  FileSpreadsheet,
  BookOpenCheck,
  Ship,
  Mail,
} from 'lucide-react';

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white border rounded-2xl p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <div className="w-9 h-9 rounded-xl bg-[#0A2342]/5 flex items-center justify-center text-[#0A2342]"><Icon className="w-5 h-5" /></div>}
      <h2 className="text-lg font-semibold text-[#0A2342]">{title}</h2>
    </div>
    <div className="text-sm text-gray-700 space-y-2 leading-relaxed">{children}</div>
  </section>
);

const Step = ({ n, children }) => (
  <div className="flex gap-3 items-start">
    <div className="shrink-0 w-6 h-6 rounded-full bg-[#FF6B35] text-white text-xs font-bold flex items-center justify-center mt-0.5">{n}</div>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

const Pill = ({ children }) => <span className="inline-block bg-slate-100 text-[#0A2342] text-xs font-medium px-2 py-0.5 rounded">{children}</span>;

const PlannerManual = () => {
  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="planner-manual">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-6 pb-6 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto">
          <Link to="/planner" className="text-sm opacity-80 flex items-center gap-1 mb-1"><ChevronLeft className="w-4 h-4" /> Back to Planner</Link>
          <h1 className="text-xl md:text-2xl font-bold">Dive Planner — User Manual</h1>
          <p className="text-xs opacity-80 mt-1">Everything you need to plan a dive day in under a minute.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-4 space-y-4">

        <Section icon={Smartphone} title="1. Install the app (recommended)">
          <p>The planner works in any browser, but installing it as an app gives you:</p>
          <ul className="list-disc pl-5">
            <li>One-tap launch from your home screen — opens straight into the planner</li>
            <li>Full-screen view with no browser bars</li>
            <li>Works offline once you've opened a trip</li>
            <li>Brighter, easier to read on the boat</li>
          </ul>
          <div className="bg-slate-50 rounded-xl p-3 space-y-2 mt-2">
            <div><strong>Android / Chrome:</strong> When you visit <Pill>/planner</Pill> you'll see an orange "Install Dive Planner" banner at the bottom. Tap <strong>Install now</strong>.</div>
            <div><strong>iPhone / iPad (Safari):</strong> Tap the <Pill>Share</Pill> icon → <strong>Add to Home Screen</strong>. The app appears on your home screen like any other app.</div>
          </div>
        </Section>

        <Section icon={Users} title="2. Get operator access">
          <Step n="1">Make sure the person who will use the planner has a regular <a className="text-[#FF6B35] underline" href="https://justdive.co.za/auth" target="_blank" rel="noreferrer">justdive.co.za</a> account.</Step>
          <Step n="2">A Just Dive admin then goes to <Pill>Admin → Planner Ops</Pill>, types their email and clicks <strong>Grant</strong>.</Step>
          <Step n="3">The operator can now log in and open <Pill>justdive.co.za/planner</Pill>.</Step>
          <p className="text-xs text-gray-500 mt-2">Operators only ever see their own trips — different operators don't see each other's data.</p>
        </Section>

        <Section icon={Calendar} title="3. Create your first trip">
          <Step n="1">Open <Pill>/planner</Pill> and tap <strong>+ New Trip</strong>.</Step>
          <Step n="2">Give it a name (e.g. "Aliwal Shoal — May") and pick the start and end dates.</Step>
          <Step n="3">Tap <strong>Create</strong>. You'll land on the trip page with 5 tabs.</Step>
        </Section>

        <Section icon={Users} title="4. Add divers">
          <Step n="1">In the trip page tap the <strong>Divers</strong> tab.</Step>
          <Step n="2">Tap <strong>+ Add Diver</strong>. Enter at least a name. Optional fields: certification, Nitrox tick, medical notes, free-text notes.</Step>
          <Step n="3">Repeat for every diver attending. There's no limit on how many you can add — the system will only place as many as your boat seats can handle.</Step>
          <p className="text-xs text-gray-500 mt-2"><strong>Skipping a dive?</strong> Open the diver and tick <Pill>Skipping dives</Pill>. They'll be left out of auto-schedules but stay on the diver list.</p>
        </Section>

        <Section icon={Link2} title="5. Buddy up couples, families & groups">
          <Step n="1">After adding divers, in the Divers tab tap <strong>Buddy up</strong>.</Step>
          <Step n="2">Tick 2 or more divers who must always dive together — couples, families, school groups, instructor pairs, anyone.</Step>
          <Step n="3">Tap <strong>Link X divers</strong>. A small orange link icon appears next to their names.</Step>
          <p>From now on, every auto-generated plan will keep them on the same launch for <strong>both dives</strong>. To unlink, tap the trash icon next to the group at the top of the tab.</p>
        </Section>

        <Section icon={Anchor} title="6. Add crew">
          <Step n="1">In the <strong>Crew</strong> tab, tap <strong>+ Add Crew</strong>.</Step>
          <Step n="2">Choose the role: <Pill>Divemaster</Pill> or <Pill>Instructor</Pill>.</Step>
          <Step n="3">Optional: pick a <strong>preferred launch</strong>. Tick <Pill>Fixed</Pill> to lock them to that launch every time. Leave blank to let the system rotate them between launches.</Step>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1">
            ⚠ <strong>Crew DO count toward boat capacity.</strong> "Capacity per launch" is total boat seats. If your boat seats 12 and you put 2 crew on a launch, the launch can hold 10 divers.
          </p>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1">
            ⚠ Every launch needs at least 1 crew member. If you have 3 launches you need at least 3 crew total (or one Fixed crew per launch).
          </p>
        </Section>

        <Section icon={FileSpreadsheet} title="6b. Import divers from your Google Form">
          <p>If your divers fill in a Google Form before the trip, you can pull all their responses straight into the planner in one tap.</p>
          <Step n="1">Open your Google Form → click <strong>Responses</strong> tab → <strong>View in Sheets</strong>.</Step>
          <Step n="2">In that Google Sheet, go to <strong>File → Share → Publish to web</strong>. Choose <strong>"Comma-separated values (.csv)"</strong> and click <strong>Publish</strong>.</Step>
          <Step n="3">Copy the URL it shows. (Edit-link URLs also work if the sheet's sharing is "Anyone with the link can view".)</Step>
          <Step n="4">In the Divers tab, tap <strong>Import divers from Google Form / Sheet</strong>, paste the URL, and tap <strong>Fetch &amp; preview</strong>.</Step>
          <Step n="5">Review the parsed list — already-existing divers are flagged. Tick or untick anyone, then <strong>Import selected</strong>. Done.</Step>
          <p className="text-xs text-gray-500">The importer auto-detects Name, Cert level, Nitrox, Buddy, Medical notes and general notes from the form column headings.</p>
        </Section>

        <Section icon={CylinderIcon} title="7. (Optional) Track cylinders">
          <Step n="1">In the <strong>Cylinders</strong> tab, tap <strong>+ Add Cylinder</strong>.</Step>
          <Step n="2">Enter a label (e.g. CYL-001), size in litres, Nitrox tick, last serviced date, next service due date.</Step>
          <Step n="3">Assign to a diver (optional) and tick <Pill>In use</Pill> when active.</Step>
          <p className="text-xs text-gray-500">The next-service date helps you spot tanks due for inspection before the day starts.</p>
        </Section>

        <Section icon={Ship} title="8. Plan the day (one or many boats)">
          <Step n="1">Go to the <strong>Days</strong> tab. Tap <strong>Plan a Day</strong>.</Step>
          <Step n="2">Pick the date. By default you'll have one boat (Boat A). To add a second boat that runs concurrently, tap <strong>+ Add boat</strong> — useful when the load is too big for one hull.</Step>
          <Step n="3">For each boat, set:
            <ul className="list-disc pl-5 mt-1">
              <li><strong>Name</strong> — what's painted on the side (e.g. "Octopus", "Stingray")</li>
              <li><strong>Launches</strong> (1–4)</li>
              <li><strong>Total seats per launch</strong> — divers + crew combined</li>
            </ul>
          </Step>
          <Step n="4">Tap <strong>Generate</strong>. The system instantly:
            <ul className="list-disc pl-5 mt-1">
              <li>Gives every diver exactly <strong>2 dives</strong></li>
              <li>Never puts the same diver twice on the same launch</li>
              <li>Keeps your buddy groups together on the <strong>same boat</strong> (the two boats run at the same time)</li>
              <li>Spreads divers evenly across boats</li>
              <li>Spreads crew so every launch has at least one</li>
              <li>Honours fixed crew assignments</li>
            </ul>
          </Step>
          <p className="text-xs text-gray-500">Why same-boat for buddies? Because the boats are launching at the same time — divers can't be on two boats at once. Buddies always stay together on one boat for both dives.</p>
        </Section>

        <Section icon={Clock} title="9. Set launch & kit-up times">
          <p>On the day-plan page, tap the <Clock className="w-3.5 h-3.5 inline" /> clock icon in the header to set a launch time for each launch (e.g. <Pill>07:30</Pill>, <Pill>09:30</Pill>, <Pill>11:30</Pill>).</p>
          <ul className="list-disc pl-5">
            <li><strong>Kit-up time is auto-calculated</strong> as launch − 1 hour, so 07:30 launch → 06:30 kit-up.</li>
            <li>Times appear on every launch card, on the PDF, and on the share link page so divers know exactly when to be ready.</li>
            <li>Leave a launch time blank to hide both the launch and kit-up labels for that launch.</li>
          </ul>
        </Section>

        <Section icon={AlertTriangle} title="10. Read the warnings">
          <p>If something doesn't fit, an amber warning banner appears at the top of the day plan. The most common ones:</p>
          <ul className="list-disc pl-5">
            <li><em>"18 divers cannot fit into 3 launches × 12 total seats after seating 2 crew…"</em> — add a launch, increase seats, reduce crew, or mark some divers as skipping.</li>
            <li><em>"Launch 2 has no crew"</em> — add crew or move one over using the <Edit2 className="w-3 h-3 inline" /> menu.</li>
            <li><em>"Launch 1 exceeds capacity"</em> — happens only after manual drag-and-drop. Move someone out.</li>
          </ul>
        </Section>

        <Section icon={Edit2} title="11. Edit a plan by hand">
          <p>Once a day is generated, you have three ways to tweak it:</p>
          <Step n="1">
            <strong>Drag & drop</strong> — touch and hold any diver or crew name, then drag to a different launch. Changes save automatically.
          </Step>
          <Step n="2">
            <strong>Tap the <Edit2 className="w-3 h-3 inline" /> on a diver</strong> → move them to another launch, remove from this launch only, or remove from the entire day.
          </Step>
          <Step n="3">
            <strong>Manage crew</strong>: tap <Pill>+ Add</Pill> at the top of any launch's CREW section to pick a crew member from the trip roster. Tap <Edit2 className="w-3 h-3 inline" /> next to an existing crew member to move or remove them.
          </Step>
          <Step n="4">
            Tap the orange <RefreshCw className="w-3 h-3 inline" /> button at the top to regenerate the whole plan if you want to start over — manual changes will be lost.
          </Step>
        </Section>

        <Section icon={BookOpenCheck} title="11b. Instructor's dive log (visible to divers immediately)">
          <p>After every launch, the dive master or instructor can quickly capture the dive's conditions so divers without computers have something to copy into their own log books.</p>
          <Step n="1">On the day plan, scroll to the launch you just completed.</Step>
          <Step n="2">Tap <strong>Add instructor's dive log</strong> at the bottom of the launch card.</Step>
          <Step n="3">Fill in the 6 fields — <strong>Date, Time, Dive site, Depth, Water temperature, Duration</strong>. Add your name so divers know who recorded it.</Step>
          <Step n="4">Tap <strong>Save log</strong>. It appears on the launch card, the share link, and the next PDF download — all in real time.</Step>
          <p className="text-xs text-gray-500 italic">
            The share view shows the exact note divers expect: <em>"NOTE: The following information is from (your name) and is here for reference only."</em>
          </p>
        </Section>

        <Section icon={Share2} title="12. Share with the boat & crew">
          <p>Four ways to share once a plan is ready:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><Download className="w-3 h-3 inline" /> <strong>Download PDF</strong> — printable A4 briefing with the Just Dive logo, launch + kit-up times, and one launch per page section (no mid-launch breaks). A small <strong>QR code in the footer</strong> links straight to the live share view of that day — divers scan it from the printed sheet to open the app version of the plan.</li>
            <li><Share2 className="w-3 h-3 inline" /> <strong>Copy share link</strong> — generates a no-login link you can paste in WhatsApp. Anyone with the link can view that day's launches and times.</li>
            <li><Eye className="w-3 h-3 inline" /> <strong>Diver Viewer Mode</strong> — when a diver opens the share link (or scans the QR), it's auto-saved to their device. They can install the same Dive Planner app and find every schedule shared with them under <Pill>My Dive Schedules</Pill>. No login required.</li>
            <li>Show the plan directly on your phone to crew before they board.</li>
          </ul>
        </Section>

        <Section icon={BookOpenCheck} title="13. My Logged Dives (for divers)">
          <p>This is what your divers see once their instructor has filled in dive logs:</p>
          <Step n="1">After visiting at least one share link, the installed app shows a <strong>"My Logged Dives"</strong> card on the home screen (and a back-link from any share view).</Step>
          <Step n="2">First time they open it they pick their name from the list (the app remembers the choice for next time).</Step>
          <Step n="3">Every dive your instructor logged for them — across every trip and share link they've saved — appears as a card with depth, time, water temp, and site.</Step>
          <Step n="4">They pick a format (<Pill>PADI</Pill>, <Pill>SSI</Pill>, <Pill>NAUI</Pill>, <Pill>ISC</Pill>, or <Pill>Plain text</Pill>) and tap <strong>Copy to log book</strong>. A formatted block lands on their clipboard, ready to paste into their certifying agency's app or paper log book.</Step>
          <p className="text-xs text-gray-500 italic">
            Every export includes the same operator-protective note divers see in-app: <em>"NOTE: The following information is from (instructor name) and is here for reference only."</em>
          </p>
          <p className="text-xs text-gray-500">This is a unique benefit — most dive operators only give a paper sheet. Just Dive operators give every diver a portable, agency-formatted log they can sync once they're home.</p>
        </Section>

        <Section icon={Mail} title="14. Email dive log recap at the end of a trip">
          <p>Want every diver to leave with their log already in their inbox — and a gentle "book again" nudge? Capture two extra pieces of info per diver:</p>
          <Step n="1">When adding (or importing) a diver, fill in their <strong>email</strong>. The CSV importer auto-detects an Email column from your Google Form.</Step>
          <Step n="2">Keep the <Pill>Send dive log by email after trip</Pill> tick on by default — divers can opt out individually.</Step>
          <Step n="3">After your last day, go to <strong>Settings tab → End-of-trip dive log emails</strong>. You'll see the three counters: <strong>opted-in</strong>, <strong>already sent</strong>, <strong>pending</strong>.</Step>
          <Step n="4">Tap <strong>Send test</strong> with your own email first — you'll receive a sample recap email rendered with one of your divers' actual logged dives. Verify it looks good.</Step>
          <Step n="5">Tap <strong>Send to N pending</strong>. Each diver gets their own personalised recap with all the dives the instructor logged for them, the protective NOTE, and a "Book your next trip with Just Dive" call-to-action.</Step>
          <p className="text-xs text-gray-500 italic">Already-emailed divers are tracked per-trip (you'll see ✓ recap sent on their card) so re-running the send only emails new additions. There's also a "Re-send to all" option if you ever need it.</p>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            ⚠ A diver needs both an <strong>email</strong> address AND <strong>at least one logged dive</strong> for them to be included in the send. Empty addresses, divers who opted out, or anyone with no instructor log get cleanly skipped.
          </p>
        </Section>

        <Section icon={Settings} title="15. Trip settings & cleanup">
          <p>The <strong>Settings</strong> tab lets you rename the trip, change dates, or delete the whole trip and all its data. Deleting is permanent — no undo.</p>
        </Section>

        <div className="bg-[#0A2342] text-white rounded-2xl p-5 text-sm">
          <h3 className="font-semibold mb-2">Need help?</h3>
          <p className="opacity-90">Email <a className="underline" href="mailto:info@justdive.co.za">info@justdive.co.za</a> or call the shop. If something looks wrong on a plan, take a screenshot — it really helps us fix it fast.</p>
        </div>

        <div className="text-center pt-4">
          <Link to="/planner">
            <Button className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="manual-back-btn">
              <ChevronLeft className="w-4 h-4 mr-2" /> Back to the Planner
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PlannerManual;
